// Translation strings - moved outside component to prevent recreation on every render.
//
// Source of truth: Master: Finalized Text / Translations for Interface (Katie S., May 2026),
// archived at /translations.pdf in the repo root. When adding new strings:
//   1. Add the English key and copy here first.
//   2. Fill in es/zh/vi/ar from the master doc.
//   3. If a key is missing for a language, callers fall back to the en value.
//
// Conventions:
// - "Family Meal Pack Program" stays in English across all languages (program-name normalization
//   per memory note).
// - Placeholders like [#], [time], [date], [restaurant] are interpolated by the calling component;
//   leave them in the string verbatim.
// System / auth error messages.
// TODO(i18n) — AWAITING TRANSLATION (Katie): these are English-only placeholders
// shown in all five languages until the Master doc supplies es/zh/vi/ar copy. They
// surface in the SMS-login and restaurant-load flows. Per the translation-ownership
// rule we do NOT invent the non-English copy; the merge loop below injects this
// English set into every language block, and any same-named key a language block
// later defines automatically wins. See pending list compiled for Katie.
export const PENDING_TRANSLATION_ERRORS = {
  errInvalidNumber: "Invalid number. Please try again.",
  errVerifyCouldNotStart: "Verification could not start. Please try again.",
  errSmsNotConfigured: "SMS service is not configured. Please contact support.",
  errSendCodeFailed: "Failed to send verification code. Please try again.",
  errEnter6Digit: "Please enter a 6-digit code",
  errAuthUnavailable: "Authentication service is not available. Please contact support.",
  errTooManyAttempts: "Too many attempts. Please wait a few minutes and try again.",
  errSmsQuota: "SMS limit reached for now. Please try again later or contact SF New Deal.",
  errSendCodeGeneric: "Could not send the code. Please check the number and try again.",
  errPhoneNotEnrolled: "This phone number is not registered with the Family Meal Pack Program.",
  errLoadRestaurants: "Failed to load restaurants. Using demo data instead.",
  errTooManyAttemptsCountdown: "Too many attempts. Please wait and try again in [time].",
  resendCodeIn: "Resend code in [time]",
  orderedTodayAt: "Ordered today at [time]",
  familyFaqCancelQ: "How do I cancel my order?",
  familyFaqCancelA: "Please be mindful when placing orders. Once you receive the order confirmation, the restaurant is notified immediately and begins preparing your meals. If you need to cancel, call the SF New Deal support line immediately — within 5 minutes of placing your order.",
  familyFaqPhoneQ: "How do I change the phone number on my account?",
  familyFaqPhoneA: "Please contact your community organization or school site lead to request changes to your enrollment information, including your phone number. They will work with the SF New Deal team to update your account.",
  familyFaqLanguageQ: "How do I change the language?",
  familyFaqLanguageA: "Use the language selector in the top right corner. The available languages are English, Chinese, Spanish, Vietnamese, and Arabic.",
  filters: "Filters",
};

const TRANSLATIONS = {
  en: {
    // Branding / program names
    title: "Family Meal Pack Program",
    programName: "Family Meal Pack Program",
    sfNewDeal: "SF New Deal",
    sfhsa: "San Francisco Human Services Agency",
    orderingPlatform: "Ordering Platform",
    restaurantDashboard: "Restaurant Dashboard",
    redemptionCard: "Redemption Card",
    smsOrderSystem: "Text message order system",
    texting: "Texting",
    sms: "SMS",
    mobileApp: "Mobile app",
    interface: "Interface",
    callCenter: "Call center",
    supportLine: "Support line",

    // Languages
    language: "Language",
    languageEnglish: "English",
    languageSpanish: "Spanish",
    languageChinese: "Chinese",
    languageArabic: "Arabic",
    languageVietnamese: "Vietnamese",

    // Login flow
    welcome: "Welcome! Please log in to order meals.",
    phoneNumber: "Phone number",
    enterPhone: "Enter your phone number",
    enterPhoneRestaurant: "Enter the phone number registered with your restaurant",
    enterEmailRestaurant: "Enter the email registered with your restaurant",
    changePhone: "Change phone number",
    changeEmail: "Change email",
    verificationCode: "Verification code",
    sendCode: "Send verification code",
    invalidCode: "Invalid verification code. Please try again.",
    sending: "Sending",
    verify: "Verify & login",
    enterCode: "Enter 6-digit code",
    enterCodeSentTo: "Enter 6-digit code sent to",
    pleaseLogin: "Please log in to view and manage your orders",
    didntReceiveCode: "Didn't receive a code? Send again",
    troubleLoggingIn: "Having trouble logging in?",
    error: "Error",
    logout: "Logout",
    logoutConfirm: "Are you sure you want to log out?",
    cancel: "Cancel",

    // Family / Participant ID
    familyId: "Family ID",
    participantId: "Participant ID",
    yourFamilyOrParticipantId: "Your Family ID / Participant ID",
    yourFamilyCode: "Your Family ID",
    showThisCode: "Show this code when picking up your order",

    // Vouchers (legacy — keep as keys for old references)
    voucher: "Voucher",
    vouchersAvailable: "[#] vouchers available until",
    vouchersUsedLegacy: "Vouchers used",
    vouchersRemainingLegacy: "Vouchers remaining",
    vouchersOfRemaining: "[#] vouchers of [#] remaining",
    noVouchersRemaining: "No vouchers remaining",
    usedAllVouchers: "This participant has used all of their available vouchers.",
    usedAllVouchersCycle: "This participant has used all of their vouchers for this meal cycle.",
    willReceiveNewVouchers: "They will receive 8 new vouchers on [date].",

    // Meals (new terminology per Alpha 3 — "meal" replaces "voucher" in user-facing copy)
    meal: "Meal",
    meals: "Meals",
    mealsAvailable: "[#] meals available until",
    mealsUsed: "Meals used",
    mealsRemaining: "Meals remaining",
    mealsOfRemaining: "[#] meals of [#] remaining",
    noMealsRemaining: "No meals remaining",
    usedAllMeals: "This participant has used all of their available meals.",
    usedAllMealsCycle: "This participant has used all of their meals for this meal cycle.",
    willReceiveNewMeals: "They will receive 8 new meals on [date].",
    vouchers: "Meals Available", // legacy header label, now reads "Meals"
    availableUntil: "available before",

    // Menu / browse / cart / orders
    menu: "Menu",
    menuItems: "Menu Items",
    selectMenuItems: "Select Menu Items",
    open: "Open",
    closed: "Closed",
    browse: "Browse",
    cart: "Cart",
    viewCart: "View cart",
    cartEmpty: "Your cart is empty",
    cartChangeHint: "To change the order, remove an item from the cart below.",
    order: "Order",
    orders: "Orders",
    addToOrder: "Add to order",
    addToCart: "Add to order",
    notEnoughMeals: "Not enough meals",
    currentlyClosed: "Currently closed",
    closedBanner: "This restaurant is currently closed. You can browse the menu, but you can only place an order during open hours.",
    yourOrder: "Your order",
    itemsOrdered: "Items ordered",
    item: "Item",
    items: "Items",
    reviewRemoveItems: "Review / remove items",
    review: "Review",
    remove: "Remove",
    placeOrder: "Place order",
    placingOrder: "Placing order",
    checkout: "Place order",
    activeOrders: "Active orders",
    ordersToday: "Today's Orders",
    previousOrders: "Past Orders",
    totalOrdersToday: "[#] total orders today",
    walkInOrder: "Walk-in order",
    redemptionCardOrder: "Redemption card order",
    myOrders: "My Orders",
    myCart: "My Cart",
    noActiveOrders: "No active orders",
    noActiveOrdersMessage: "You don't have any orders waiting for pickup. Browse restaurants to place a new order!",
    browseRestaurants: "Browse restaurants",
    onlyOneRestaurantAtATime: "Orders are for pickup from one restaurant at a time.",
    orderNumber: "Order #",
    orderStatus: "Status",
    statusPending: "Pending",
    statusConfirmed: "Confirmed",
    statusReady: "Ready for pickup",
    confirmed: "Confirmed",
    canceled: "Canceled",
    cancelOrder: "Cancel order",
    waiting: "Waiting",
    completed: "Completed",
    pickedUp: "Picked up",
    readyForPickup: "Ready for pickup",
    markAsPickedUp: "Mark as picked up",
    callRestaurant: "Call restaurant",
    loading: "Loading restaurants...",
    orderSuccess: "Order placed successfully!",
    orderAddedToActive: "The order has been added to your active orders.",
    redirectingToActiveOrders: "Redirecting to Active Orders...",
    pleasePrepareOrder: "Please prepare the order immediately.",

    // Filters
    search: "Search restaurants...",
    allNeighborhoods: "All neighborhoods",
    filterByNeighborhood: "Filter by area",
    allCuisines: "All cuisines",
    filterByCuisine: "Filter by cuisine",
    otherFilters: "Other filters",
    filterBy: "Filter by",
    restaurantHours: "Restaurant hours",
    noRestaurantsFiltered: "No restaurants match your filters",
    noRestaurantsFilteredMessage: "Try adjusting your neighborhood or cuisine filters to see more options.",
    clearFilters: "Clear all filters",

    // Confirmation / pickup
    orderConfirmed: "Your order is confirmed!",
    orderPlaced: "Order placed",
    orderPlacedAt: "Order placed at",
    orderPlacedAtRestaurant: "Order placed at [restaurant]",
    orderDetails: "Order details",
    orderedAt: "Ordered on",
    pickupAt: "Pickup at",
    pickupBy: "Pickup by",
    pickupOnly: "Pickup only",
    pickupInstructions: "Pickup instructions",
    pickupLocation: "Pickup location",
    pickupWindow: "Pickup window",
    restaurantLocation: "Pickup location",
    getDirections: "Get directions",
    youNowHave: "You now have",
    mealsLeftBefore: "meals left before",
    mealLeftBefore: "meal left before",
    total: "Total",
    pickupWindowSentence: "You can pick up your order in [time] and no later than [time], unless the restaurant closes sooner.",
    inOneHour: "1 hour",
    in30Minutes: "30 minutes",
    in60Minutes: "60 minutes",
    noLaterThan: "no later than 90 minutes",
    in90Minutes: "90 minutes",
    unlessCloseSooner: "unless they close sooner",
    asSoonAsPossible: "As soon as possible",
    pickupAsap: "Pickup as soon as possible",
    familyMealLastOrder: "Family Meals last order at",
    pickedUpBadge: "Picked up",
    readyForPickup: "Ready for pickup",
    goHome: "Go to Home",
    back: "Back",

    // Help
    help: "Help",
    needHelp: "Need help?",
    contactSupport: "Contact Support",
    callUs: "SF New Deal Support Line",
    officeHours: "SF New Deal office hours",
    mondayFriday: "Monday - Friday",
    hours: "9:00 AM - 5:00 PM",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    voicemailInstructions: "If nobody is available to assist or you are calling outside of these hours, leave a voicemail with your name and phone number, and someone from our team will call you back.",
    callCenterTagline: "Order delicious, culturally relevant meals from 30+ restaurants across San Francisco.",
    needSupport: "If you need support, please contact the SF New Deal support line at",
    contactCallCenter: "Please contact the SF New Deal call center at (415) 480-1185. Extension #2 for Cantonese, #3 for Mandarin, #4 for Spanish.",
    extensions: "Extension #2 for Cantonese, #3 for Mandarin, #4 for Spanish.",
    commonQuestions: "Common Questions",
    howToOrder: "How do I order meals?",
    howToOrderAnswer: "Browse available restaurants, add items to your cart, and click 'Place Order'. You'll receive a confirmation with pickup instructions.",
    wherePickup: "Where do I pick up my order?",
    wherePickupAnswer: "Your confirmation screen shows the restaurant address and a 'Get Directions' button to help you find it.",
    whenPickup: "When can I pick up my order?",
    whenPickupAnswer: "For orders between 1 and 6 meals, you can pick up your meals after 30 minutes. For orders greater than 6 meals, you can pick up 60 minutes after placing the order. You must pick up within 90 minutes of placing your order, unless the restaurant is closing sooner.",
    whatBring: "What do I do when picking up my meals?",
    whatBringAnswer: "When you arrive at the restaurant, tell staff that you are from the Family Meal Pack Program and share your Family ID / Participant ID. They will match you to the correct order and hand you your meals.",
    howManyMeals: "How many meals can I order?",
    howManyMealsAnswer: "Check your available meals at the top of the home screen.",
    mealsResetSentence: "Your meals reset at the beginning of every 2-week cycle. Meals do not transfer or roll over from one cycle to the next.",
    cycleDatesSentence: "Cycle start and end dates can be found here.",

    // Walk-in two-step verification
    verifyWithTwoStep: "Verify the participant with a two-step process",
    step1EnterFamilyId: "Step 1: Enter their Family ID / Participant ID",
    askParticipantForFamilyId: "Ask the participant for their Family ID / Participant ID",
    verifyFamilyIdButton: "Verify Family ID / Participant ID",
    verifying: "Verifying",
    familyIdVerified: "Family ID / Participant ID verified",
    familyIdNotFound: "Family ID / Participant ID not found. Please re-check with the family and re-enter.",
    step2VerifyPhone: "Step 2: Verify Phone Number",
    askLastFourDigits: "Ask the participant for the last 4 digits of their phone number",
    lastFourDigits: "Last 4 digits",
    phoneDoesNotMatch: "Phone number does not match. Please reconfirm the last 4 digits with the participant.",
    startOver: "Start Over",

    // Dietary
    dietaryConsiderations: "Dietary considerations",
    dietaryRestrictions: "Dietary restrictions",
    yourDietaryRestrictions: "Your dietary restrictions",
    noDietaryRestrictions: "No dietary restrictions",
    dietVegetarian: "Vegetarian",
    dietVegan: "Vegan",
    dietNutAllergy: "Nut Allergy",
    dietSeafoodAllergy: "Seafood Allergy",
    dietLactoseIntolerant: "Lactose Intolerant",
    dietGlutenAllergy: "Gluten Allergy",
    dietDiabetic: "Diabetic",
    dietHalal: "Halal",
    dietNoPork: "No Pork",
    dietCitrusAllergy: "Citrus Allergy",
    dietNoEgg: "No Egg",
    dietSoyAllergy: "Soy Allergy",
    dietAvocadoAllergy: "Avocado Allergy",
    dietScallopAllergy: "Scallop Allergy",
    dietCinnamonAllergy: "Cinnamon Allergy",
    dietGrapeseedOilAllergy: "Grapeseed Oil Allergy",
    dietPineappleAllergy: "Pineapple Allergy",
    dietHoneyAllergy: "Honey Allergy",
    dietFavaBeansAllergy: "Fava Beans Allergy",
    dietTomatoAllergy: "Tomato Allergy",
    dietOtherAllergy: "Other Allergy",
    dietNoBeef: "No Beef",
    dietDairyFree: "Dairy Free",
    dietGlutenFree: "Gluten Free",

    // Cuisines
    cuisine: "Cuisine",
    cuisineAmerican: "American",
    cuisineChinese: "Chinese",
    cuisineJapanese: "Japanese",
    cuisineBurmese: "Burmese",
    cuisineLatinAmerican: "Latin American",
    cuisineGuatemalan: "Guatemalan",
    cuisineMexican: "Mexican",
    cuisineSalvadoran: "Salvadoran",
    cuisineVenezuelan: "Venezuelan",
    cuisineIndian: "Indian",
    cuisineMediterranean: "Mediterranean",
    cuisineMiddleEastern: "Middle Eastern",
    cuisineNorthAfrican: "North African",
    cuisineYemeni: "Yemeni",
    cuisineVietnamese: "Vietnamese",
    cuisineFusion: "Fusion"
  },

  es: {
    title: "El Programa de Comidas Familiares",
    programName: "El Programa de Comidas Familiares",
    sfNewDeal: "SF New Deal",
    sfhsa: "La Agencia de Servicios Humanos de San Francisco",
    orderingPlatform: "Plataforma de pedidos",
    restaurantDashboard: "Panel de control del restaurante",
    redemptionCard: "Tarjeta de Canje",
    smsOrderSystem: "Sistema de pedidos por mensaje de texto",
    texting: "Texto",
    sms: "SMS",
    mobileApp: "Aplicación móvil",
    interface: "Interfaz",
    callCenter: "Centro de llamadas",
    supportLine: "Línea de asistencia",

    language: "Idioma",
    languageEnglish: "Inglés",
    languageSpanish: "Español",
    languageChinese: "Chino",
    languageArabic: "Árabe",
    languageVietnamese: "Vietnamita",

    welcome: "¡Bienvenido! Inicie su sesión para realizar un pedido.",
    phoneNumber: "Número de teléfono",
    enterPhone: "Ingrese su número de teléfono",
    enterPhoneRestaurant: "Ingrese el número de teléfono registrado con su restaurante",
    enterEmailRestaurant: "Ingrese el correo electrónico registrado con su restaurante",
    changePhone: "Cambiar número de teléfono",
    changeEmail: "Cambiar correo electrónico",
    verificationCode: "Código de verificación",
    sendCode: "Enviar código de verificación",
    invalidCode: "El código de verificación no es válido. Por favor inténtelo de nuevo.",
    sending: "Enviando",
    verify: "Verificar e iniciar sesión",
    enterCode: "Ingrese el código de 6 dígitos",
    enterCodeSentTo: "Ingrese el código de 6 dígitos enviado a",
    pleaseLogin: "Por favor inicie su sesión para ver y gestionar sus pedidos",
    didntReceiveCode: "¿No recibió el código? Enviar de nuevo",
    troubleLoggingIn: "¿Tiene problemas para iniciar sesión?",
    error: "Error",
    logout: "Cerrar Sesión",
    logoutConfirm: "¿Está seguro de que desea cerrar sesión?",
    cancel: "Cancelar",

    familyId: "Identificación familiar",
    participantId: "Identificación del participante",
    yourFamilyOrParticipantId: "Su identificación familiar / identificación del participante",
    yourFamilyCode: "Tu ID Familiar",
    showThisCode: "Muestre este código al recoger su pedido",

    voucher: "Vale",
    vouchersAvailable: "[#] vales disponibles hasta el",
    vouchersUsedLegacy: "vales utilizados",
    vouchersRemainingLegacy: "vales restantes",
    vouchersOfRemaining: "Quedan [#] vales de [#]",
    noVouchersRemaining: "No quedan vales disponibles",
    usedAllVouchers: "Este participante ya usó todos sus cupones disponibles.",
    usedAllVouchersCycle: "Este participante ya usó todos sus vales para este ciclo de comidas.",
    willReceiveNewVouchers: "Recibirá 8 vales nuevos el [date].",

    meal: "Comida",
    meals: "Comidas",
    mealsAvailable: "[#] comidas disponibles hasta el",
    mealsUsed: "Comidas utilizadas",
    mealsRemaining: "Comidas restantes",
    mealsOfRemaining: "Quedan [#] comidas de [#]",
    noMealsRemaining: "No quedan comidas disponibles",
    usedAllMeals: "Este participante ya usó todas sus comidas disponibles.",
    usedAllMealsCycle: "Este participante ya usó todas sus comidas para este ciclo de comidas.",
    willReceiveNewMeals: "Recibirá 8 comidas nuevas el [date].",
    vouchers: "Comidas disponibles",
    availableUntil: "disponibles antes de",

    menu: "Menú",
    menuItems: "Artículos del menú",
    selectMenuItems: "Seleccionar artículos del menú",
    open: "Abierto",
    closed: "Cerrado",
    browse: "Explorar",
    cart: "Carrito",
    viewCart: "Ver carrito",
    cartEmpty: "Su carrito está vacío",
    cartChangeHint: "Para cambiar el pedido, elimine un artículo del carrito a continuación.",
    order: "Pedido",
    orders: "Pedidos",
    addToOrder: "Agregar al pedido",
    addToCart: "Agregar al pedido",
    notEnoughMeals: "Comidas insuficientes",
    currentlyClosed: "Cerrado ahora",
    closedBanner: "Este restaurante está cerrado en este momento. Puede ver el menú, pero solo puede hacer un pedido durante el horario de atención.",
    yourOrder: "Su pedido",
    itemsOrdered: "Artículos pedidos",
    item: "Artículo",
    items: "Artículos",
    reviewRemoveItems: "Revisar / eliminar artículos",
    review: "Revisar",
    remove: "Eliminar",
    placeOrder: "Realizar pedido",
    placingOrder: "Realizando pedido",
    checkout: "Realizar pedido",
    activeOrders: "Pedidos activos",
    ordersToday: "Pedidos de hoy",
    previousOrders: "Pedidos anteriores",
    totalOrdersToday: "[#] pedidos totales de hoy",
    walkInOrder: "Pedido en el local",
    redemptionCardOrder: "Pedido con tarjeta de canje",
    myOrders: "Mis Pedidos",
    myCart: "Mi Carrito",
    noActiveOrders: "No hay pedidos activos",
    noActiveOrdersMessage: "No tienes pedidos esperando para recoger. ¡Explora restaurantes para hacer un nuevo pedido!",
    browseRestaurants: "Explorar restaurantes",
    onlyOneRestaurantAtATime: "Los pedidos deben recogerse en un solo restaurante a la vez.",
    orderNumber: "Pedido #",
    orderStatus: "Estado",
    statusPending: "Pendiente",
    statusConfirmed: "Confirmado",
    statusReady: "Listo para recoger",
    confirmed: "Confirmado",
    canceled: "Cancelado",
    cancelOrder: "Cancelar pedido",
    waiting: "En espera",
    completed: "Completado",
    pickedUp: "Recogido",
    readyForPickup: "Listo para recoger",
    markAsPickedUp: "Marcar como recogido",
    callRestaurant: "Llamar al Restaurante",
    loading: "Cargando restaurantes...",
    orderSuccess: "¡El pedido se realizó correctamente!",
    orderAddedToActive: "El pedido se agregó a sus pedidos activos.",
    redirectingToActiveOrders: "Redirigiendo a Pedidos Activos...",
    pleasePrepareOrder: "Prepare el pedido de inmediato.",

    search: "Buscar restaurantes...",
    allNeighborhoods: "Todos los barrios",
    filterByNeighborhood: "Filtrar por área",
    allCuisines: "Todas las cocinas",
    filterByCuisine: "Filtrar por cocina",
    otherFilters: "Otros filtros",
    filterBy: "Filtrar por",
    restaurantHours: "Horario del restaurante",
    noRestaurantsFiltered: "No hay restaurantes con estos filtros",
    noRestaurantsFilteredMessage: "Intenta ajustar tus filtros de barrio o cocina para ver más opciones.",
    clearFilters: "Borrar todos los filtros",

    orderConfirmed: "¡Su pedido está confirmado!",
    orderPlaced: "Pedido realizado",
    orderPlacedAt: "Pedido realizado a las",
    orderPlacedAtRestaurant: "Pedido realizado en [restaurant]",
    orderDetails: "Detalles del pedido",
    orderedAt: "Pedido a las",
    pickupAt: "Recoge a las",
    pickupBy: "Recoge antes de las",
    pickupOnly: "Solo para recoger",
    pickupInstructions: "Instrucciones de recogida",
    pickupLocation: "Lugar de recogida",
    pickupWindow: "Horario de recogida",
    restaurantLocation: "Lugar de recogida",
    getDirections: "Cómo llegar",
    youNowHave: "Actualmente le quedan",
    mealsLeftBefore: "comidas antes del",
    mealLeftBefore: "comida antes del",
    total: "Total",
    pickupWindowSentence: "Puede recoger su pedido en [time] y a más tardar a las [time], a menos que el restaurante cierre antes.",
    inOneHour: "1 hora",
    in30Minutes: "30 minutos",
    in60Minutes: "60 minutos",
    noLaterThan: "a más tardar en 90 minutos",
    in90Minutes: "90 minutos",
    unlessCloseSooner: "a menos que cierren antes",
    asSoonAsPossible: "Lo antes posible",
    pickupAsap: "Recoger lo antes posible",
    familyMealLastOrder: "Último pedido de Family Meals a las",
    pickedUpBadge: "Recogido",
    readyForPickup: "Listo para recoger",
    goHome: "Ir al Inicio",
    back: "Atrás",

    help: "Ayuda",
    needHelp: "¿Necesita ayuda?",
    contactSupport: "Contactar Soporte",
    callUs: "Línea de asistencia de SF New Deal",
    officeHours: "Horario de atención de la oficina de SF New Deal",
    mondayFriday: "Lunes - Viernes",
    hours: "9:00 AM - 5:00 PM",
    monday: "Lunes",
    tuesday: "Martes",
    wednesday: "Miércoles",
    thursday: "Jueves",
    friday: "Viernes",
    saturday: "Sábado",
    sunday: "Domingo",
    voicemailInstructions: "Si no hay nadie disponible para atenderle o llama fuera de este horario, deje un mensaje de voz con su nombre y número de teléfono, y alguien de nuestro equipo le devolverá la llamada.",
    callCenterTagline: "Encargue deliciosas comidas con relevancia cultural de más de 30 restaurantes de San Francisco.",
    needSupport: "Si necesita ayuda, llame a la línea de asistencia de SF New Deal al",
    contactCallCenter: "Comuníquese con el centro de llamada al (415) 480-1185, #4 para español.",
    extensions: "Extensión #2 para cantonés, #3 para mandarín, #4 para español.",
    commonQuestions: "Preguntas Frecuentes",
    howToOrder: "¿Dónde puedo ordenar mis comidas?",
    howToOrderAnswer: "Explore los restaurantes disponibles, agregue artículos a su carrito y haga clic en 'Realizar pedido'. Recibirá una confirmación con las instrucciones para recogerlo.",
    wherePickup: "¿Dónde recojo mi pedido?",
    wherePickupAnswer: "Su pantalla de confirmación muestra la dirección del restaurante y un botón 'Cómo llegar' para ayudarle a llegar a él.",
    whenPickup: "¿Cuándo puedo recoger mi pedido?",
    whenPickupAnswer: "Para pedidos de entre 1 y 6 comidas, puede recogerlas después de 30 minutos. Para pedidos de más de 6 comidas, puede recogerlas 60 minutos después de realizar el pedido. Debe recogerlas dentro de los 90 minutos de confirmar su pedido, a menos que el restaurante cierre antes.",
    whatBring: "¿Qué debo hacer al recoger mis comidas?",
    whatBringAnswer: "Al llegar al restaurante, indique al personal del restaurante que usted es parte del Programa de Comidas Familiares y comparta su ID familiar/participante único. Ellos identificarán su orden y le entregarán sus comidas.",
    howManyMeals: "¿Cuántas comidas puedo pedir?",
    howManyMealsAnswer: "Verifique sus comidas disponibles en la parte superior de la pantalla de inicio.",
    mealsResetSentence: "La cantidad de comidas se reinicia cada dos semanas. No se pueden pasar o transferir comidas de un ciclo al siguiente.",
    cycleDatesSentence: "Las fechas de inicio y finalización del ciclo se pueden encontrar aquí.",

    verifyWithTwoStep: "Verifique al participante mediante un proceso de dos pasos",
    step1EnterFamilyId: "Paso 1: Ingrese su identificación familiar / Identificación del participante",
    askParticipantForFamilyId: "Pídale al participante su identificación familiar / Identificación del participante",
    verifyFamilyIdButton: "Verificar identificación familiar / Identificación del participante",
    verifying: "Verificando",
    familyIdVerified: "Identificación familiar / Identificación del participante verificada",
    familyIdNotFound: "No se encontró la identificación familiar / Identificación del participante. Por favor, vuelva a verificar con la familia e inténtelo de nuevo.",
    step2VerifyPhone: "Paso 2: Verifique el número de teléfono",
    askLastFourDigits: "Pida al participante los últimos 4 dígitos de su número de teléfono",
    lastFourDigits: "Últimos 4 dígitos",
    phoneDoesNotMatch: "El número de teléfono no coincide. Vuelva a confirmar los últimos 4 dígitos con el participante.",
    startOver: "Empezar de nuevo",

    dietaryConsiderations: "Consideraciones dietéticas",
    dietaryRestrictions: "Restricciones dietéticas",
    yourDietaryRestrictions: "Sus restricciones dietéticas",
    noDietaryRestrictions: "Sin restricciones dietéticas",
    dietVegetarian: "Vegetariano",
    dietVegan: "Vegano",
    dietNutAllergy: "Alergia (Nueces)",
    dietSeafoodAllergy: "Alergia (Mariscos)",
    dietLactoseIntolerant: "Intolerante a Lactosa",
    dietGlutenAllergy: "Alergia (Gluten)",
    dietDiabetic: "Diabético",
    dietHalal: "Halal",
    dietNoPork: "Sin Cerdo",
    dietCitrusAllergy: "Alergia (Cítricos)",
    dietNoEgg: "Sin Huevo",
    dietSoyAllergy: "Alergia (Soja)",
    dietAvocadoAllergy: "Alergia (Aguacate)",
    dietScallopAllergy: "Alergia (Vieiras)",
    dietCinnamonAllergy: "Alergia (Canela)",
    dietGrapeseedOilAllergy: "Alergia (Aceite de Semilla de Uva)",
    dietPineappleAllergy: "Alergia (Piña)",
    dietHoneyAllergy: "Alergia (Miel)",
    dietFavaBeansAllergy: "Alergia (Habas)",
    dietTomatoAllergy: "Alergia (Tomate)",
    dietOtherAllergy: "Otras Alergias",
    dietNoBeef: "Sin Carne de Res",
    dietDairyFree: "Sin Lácteos",
    dietGlutenFree: "Sin Gluten",

    cuisine: "Cocina",
    cuisineAmerican: "Americano",
    cuisineChinese: "Chino",
    cuisineJapanese: "Japonés",
    cuisineBurmese: "Birmana",
    cuisineLatinAmerican: "Latinoamericana",
    cuisineGuatemalan: "Guatemalteco",
    cuisineMexican: "Mexicano",
    cuisineSalvadoran: "Salvadoreño",
    cuisineVenezuelan: "Venezolano",
    cuisineIndian: "Indio",
    cuisineMediterranean: "Mediterráneo",
    cuisineMiddleEastern: "Medio Oriente",
    cuisineNorthAfrican: "Norteafricano",
    cuisineYemeni: "Yemení",
    cuisineVietnamese: "Vietnamita",
    cuisineFusion: "Fusión"
  },

  zh: {
    title: "家庭餐活動",
    programName: "家庭餐活動",
    sfNewDeal: "三藩市新交易",
    sfhsa: "三藩市福利局(SFHSA)",
    orderingPlatform: "點餐平台",
    restaurantDashboard: "餐廳公告版/點餐介面",
    redemptionCard: "免費餐卡",
    smsOrderSystem: "短信訂餐系統",
    texting: "發送短信",
    sms: "短信",
    mobileApp: "手機應用軟件",
    interface: "界面",
    callCenter: "熱線呼叫中心",
    supportLine: "服務專線",

    language: "語言",
    languageEnglish: "英文",
    languageSpanish: "西班牙語",
    languageChinese: "中文",
    languageArabic: "阿拉伯語",
    languageVietnamese: "越南語",

    welcome: "歡迎您！請登錄點餐",
    phoneNumber: "電話號碼",
    enterPhone: "輸入您的電話號碼",
    enterPhoneRestaurant: "輸入您餐廳註冊使用的電話號碼",
    enterEmailRestaurant: "輸入您餐廳註冊的電子郵件地址",
    changePhone: "更換電話號碼",
    changeEmail: "更換電子郵件地址",
    verificationCode: "驗證碼",
    sendCode: "發送驗證碼",
    invalidCode: "驗證碼無效，請再試一次。",
    sending: "發送中",
    verify: "驗證 & 登錄",
    enterCode: "輸入6位驗證碼",
    enterCodeSentTo: "輸入6位驗證碼發送至",
    pleaseLogin: "請登入查詢並管理您的訂單",
    didntReceiveCode: "沒收到驗證碼了嗎？發送新的驗證碼",
    troubleLoggingIn: "無法登錄？",
    error: "錯誤",
    logout: "登出",
    logoutConfirm: "您確定要登出嗎？",
    cancel: "取消",

    familyId: "家庭餐ID",
    participantId: "參加活動者ID",
    yourFamilyOrParticipantId: "家庭餐/參加活動者ID",
    yourFamilyCode: "您的家庭ID",
    showThisCode: "取餐時請出示您的驗證碼",

    voucher: "兌換券",
    vouchersAvailable: "[Date] 前有 [#] 兌換券可使用",
    vouchersUsedLegacy: "已使用的兌換券",
    vouchersRemainingLegacy: "剩下的兌換券",
    vouchersOfRemaining: "[#] 免費餐券中剩到 [#] 張可以使用",
    noVouchersRemaining: "免費餐券已用完",
    usedAllVouchers: "活動參加者免費餐券已用完。",
    usedAllVouchersCycle: "活動參加者本週期免費餐券已用完",
    willReceiveNewVouchers: "活動參加者會與 [date] 收到8張新的免費餐券",

    meal: "餐點",
    meals: "餐點",
    mealsAvailable: "到 [Date] 為止有 [#] 份餐點可以使用",
    mealsUsed: "已兌換的餐點",
    mealsRemaining: "剩餘的餐點",
    mealsOfRemaining: "[#] 份還剩下 [#] 份剩餘餐點",
    noMealsRemaining: "沒有剩餘餐點",
    usedAllMeals: "活動參加者免費餐點已用完",
    usedAllMealsCycle: "活動參加者本週期剩餘餐點已用完",
    willReceiveNewMeals: "他們會於 [Date] 收到八份新的餐點",
    vouchers: "可用餐點",
    availableUntil: "可用前",

    menu: "菜單",
    menuItems: "菜單選項",
    selectMenuItems: "選擇菜單選項",
    open: "打開",
    closed: "關閉",
    browse: "瀏覽",
    cart: "購物車",
    viewCart: "查看購物車",
    cartEmpty: "您的購物車是空的",
    cartChangeHint: "修改訂單，從以下購物車移除菜單選項",
    order: "訂單",
    orders: "訂單",
    addToOrder: "加入訂單",
    addToCart: "加入訂單",
    notEnoughMeals: "餐點不足",
    currentlyClosed: "目前已關閉",
    closedBanner: "該餐廳目前已關閉。您可以瀏覽菜單，但只能在營業時間內下單。",
    yourOrder: "您的訂單",
    itemsOrdered: "已下單",
    item: "菜單選項",
    items: "菜單選項",
    reviewRemoveItems: "瀏覽/ 移除菜單選項",
    review: "瀏覽",
    remove: "取消",
    placeOrder: "訂餐",
    placingOrder: "訂餐中",
    checkout: "訂餐",
    activeOrders: "已下訂單",
    ordersToday: "今天的訂單",
    previousOrders: "歷史訂單",
    totalOrdersToday: "[#] 今天總訂單數",
    walkInOrder: "現場點餐",
    redemptionCardOrder: "免費餐卡訂單",
    myOrders: "我的訂單",
    myCart: "我的購物車",
    noActiveOrders: "沒有活躍訂單",
    noActiveOrdersMessage: "您目前沒有等待取餐的訂單。瀏覽餐廳以下新訂單！",
    browseRestaurants: "瀏覽餐廳",
    onlyOneRestaurantAtATime: "一個餐廳每次可領取一個訂單。",
    orderNumber: "訂單號 #",
    orderStatus: "狀態",
    statusPending: "待處理",
    statusConfirmed: "已確認",
    statusReady: "備餐完成",
    confirmed: "已確認",
    canceled: "已取消",
    cancelOrder: "取消訂單",
    waiting: "等待中",
    completed: "已完成",
    pickedUp: "已取餐",
    readyForPickup: "備餐完成",
    markAsPickedUp: "標記為已取餐",
    callRestaurant: "致電餐廳",
    loading: "加載餐廳中...",
    orderSuccess: "訂餐成功！",
    orderAddedToActive: "本訂單被加入了您的有效訂單中",
    redirectingToActiveOrders: "重新跳到到有效訂單",
    pleasePrepareOrder: "請馬上備餐",

    search: "搜索餐廳...",
    allNeighborhoods: "所有的鄰里社區",
    filterByNeighborhood: "按地區篩選",
    allCuisines: "所有菜式",
    filterByCuisine: "按菜式篩選",
    otherFilters: "其它篩選項",
    filterBy: "按照篩選",
    restaurantHours: "餐廳營業時間",
    noRestaurantsFiltered: "沒有符合篩選條件的餐廳",
    noRestaurantsFilteredMessage: "請嘗試調整您的社區或菜系篩選以查看更多選項。",
    clearFilters: "清除所有篩選",

    orderConfirmed: "您的訂單已確認！",
    orderPlaced: "訂單已下",
    orderPlacedAt: "下單時間",
    orderPlacedAtRestaurant: "已在 [restaurant] 下單",
    orderDetails: "訂單明細",
    orderedAt: "下單時間",
    pickupAt: "取餐時間",
    pickupBy: "之前必須取餐",
    pickupOnly: "只可取餐",
    pickupInstructions: "取餐說明",
    pickupLocation: "取餐地點",
    pickupWindow: "取餐窗口",
    restaurantLocation: "取餐地點",
    getDirections: "獲得方向",
    youNowHave: "您在",
    mealsLeftBefore: "之前還剩 [#] 份餐點",
    mealLeftBefore: "之前還剩 [#] 份餐點",
    total: "總數",
    pickupWindowSentence: "您可以在 [time] 取餐後 不晚於 [time] 除非餐廳提前下班。",
    inOneHour: "1小時",
    in30Minutes: "30分鐘",
    in60Minutes: "60分鐘",
    noLaterThan: "最晚90分鐘內",
    in90Minutes: "90分鐘",
    unlessCloseSooner: "除非提前關閉",
    asSoonAsPossible: "請盡快取餐",
    pickupAsap: "請盡快取餐",
    familyMealLastOrder: "Family Meals 最後點餐時間",
    pickedUpBadge: "已取餐",
    readyForPickup: "可取餐",
    goHome: "返回主介面",
    back: "返回",

    help: "幫助",
    needHelp: "需要幫助？",
    contactSupport: "聯繫支持",
    callUs: "SF New Deal 服務專線",
    officeHours: "三藩市新交易工作時間",
    mondayFriday: "週一至週五",
    hours: "上午9:00 - 下午5:00",
    monday: "週一",
    tuesday: "週二",
    wednesday: "週三",
    thursday: "週四",
    friday: "週五",
    saturday: "週六",
    sunday: "週日",
    voicemailInstructions: "如果暫時無人接聽或您在營業時間之外致電，請在語音信箱留下您的姓名、我們的團隊會給您回電。",
    callCenterTagline: "從三藩市的30多家餐廳訂美味且有文化特色的餐點。",
    needSupport: "如需幫助，請致電SF New Deal服務專線",
    contactCallCenter: "請聯繫我們的呼叫中心電話 (415) 480-1185。選擇分機號2為粵語，3為普通話。",
    extensions: "選擇分機號2為粵語，3為普通話。",
    commonQuestions: "常見諮詢問題",
    howToOrder: "我該如何點餐？",
    howToOrderAnswer: "瀏覽我們活動的可用餐廳，將菜單選項加入購物車，並點擊\"訂餐\"。您將會收到包括取餐說明的確認信息。",
    wherePickup: "我在哪裡取餐？",
    wherePickupAnswer: "您的確認介面會顯示餐廳地址以及 \"定位方向\" 按鈕。怠您找到餐廳。",
    whenPickup: "我可以什麼時候取餐？",
    whenPickupAnswer: "如果您的訂餐數量是1-6份之間，通常30分鐘之後您可以取餐。如果訂餐數量多於6份，您可以在訂餐60分鐘之內取餐。您必須在訂餐90分鐘內取餐，否則餐廳營業時間可能更早關閉。",
    whatBring: "我要取餐的時候應該做什麼？",
    whatBringAnswer: "當您到達餐廳時，請告知餐廳工作人員您參加了家庭餐活動並且分享您獨有的家庭/參與者ID。他們會找到對應您的訂單，並將餐點交給您。",
    howManyMeals: "我可以訂多少份餐？",
    howManyMealsAnswer: "在您的主介面上面瀏覽可點的套餐",
    mealsResetSentence: "您可領餐的數量每兩週重置一次。以兩週為週期。兩週之內沒有領取的套餐不會延續到下一個週期。",
    cycleDatesSentence: "點餐週期的起始日期可以在這裡查詢。",

    verifyWithTwoStep: "通過雙重驗證確認活動參與者身份",
    step1EnterFamilyId: "第一步：輸入家庭ID/活動參加者ID",
    askParticipantForFamilyId: "詢問活動參加者的家庭ID/活動參加者ID",
    verifyFamilyIdButton: "驗證家庭ID/活動參加者ID",
    verifying: "驗證",
    familyIdVerified: "家庭ID/活動參加者ID已核實",
    familyIdNotFound: "家庭ID/活動參加者ID未找到。請和家庭重新核實並再次輸入ID",
    step2VerifyPhone: "第二步：驗證電話號碼",
    askLastFourDigits: "詢問活動參加者最後4位電話號碼",
    lastFourDigits: "後4位電話號碼",
    phoneDoesNotMatch: "電話號碼不匹配，請和活動參加者再次確認後4位號碼。",
    startOver: "重新開始",

    dietaryConsiderations: "飲食考慮",
    dietaryRestrictions: "飲食禁忌",
    yourDietaryRestrictions: "您的飲食禁忌",
    noDietaryRestrictions: "沒有飲食禁忌",
    dietVegetarian: "素食",
    dietVegan: "全素食，不含蛋奶",
    dietNutAllergy: "堅果過敏",
    dietSeafoodAllergy: "海鮮過敏",
    dietLactoseIntolerant: "乳糖不耐受",
    dietGlutenAllergy: "麩質過敏",
    dietDiabetic: "糖尿病患者",
    dietHalal: "清真",
    dietNoPork: "不要豬肉",
    dietCitrusAllergy: "柑橘類過敏",
    dietNoEgg: "不要雞蛋",
    dietSoyAllergy: "豆製品過敏",
    dietAvocadoAllergy: "牛油果過敏",
    dietScallopAllergy: "扇貝過敏",
    dietCinnamonAllergy: "肉桂過敏",
    dietGrapeseedOilAllergy: "葡萄籽油過敏",
    dietPineappleAllergy: "菠蘿過敏",
    dietHoneyAllergy: "蜂蜜過敏",
    dietFavaBeansAllergy: "蠶豆",
    dietTomatoAllergy: "番茄過敏",
    dietOtherAllergy: "其他過敏",
    dietNoBeef: "不要牛肉",
    dietDairyFree: "不含乳製品",
    dietGlutenFree: "無麩質",

    cuisine: "菜式",
    cuisineAmerican: "美式料理",
    cuisineChinese: "中式料理",
    cuisineJapanese: "日式料理",
    cuisineBurmese: "緬甸料理",
    cuisineLatinAmerican: "拉美料理",
    cuisineGuatemalan: "危地馬拉料理",
    cuisineMexican: "墨西哥料理",
    cuisineSalvadoran: "薩瓦爾多料理",
    cuisineVenezuelan: "委內瑞拉料理",
    cuisineIndian: "印度料理",
    cuisineMediterranean: "地中海料理",
    cuisineMiddleEastern: "中東料理",
    cuisineNorthAfrican: "北非料理",
    cuisineYemeni: "也門料理",
    cuisineVietnamese: "越南料理",
    cuisineFusion: "融合菜"
  },

  vi: {
    title: "Family Meal Pack Program",
    programName: "Family Meal Pack Program",
    sfNewDeal: "SF New Deal",
    sfhsa: "San Francisco Human Services Agency",
    orderingPlatform: "Nền tảng đặt hàng",
    restaurantDashboard: "Bảng điều khiển của nhà hàng",
    redemptionCard: "Thẻ quy đổi",
    smsOrderSystem: "Hệ thống đặt bữa ăn qua tin nhắn",
    texting: "Nhắn tin",
    sms: "SMS",
    mobileApp: "Ứng dụng di động",
    interface: "Giao diện",
    callCenter: "Trung tâm tổng đài",
    supportLine: "Đường dây hỗ trợ",

    language: "Ngôn ngữ",
    languageEnglish: "Tiếng Anh",
    languageSpanish: "Tiếng Tây Ban Nha",
    languageChinese: "Tiếng Trung Quốc",
    languageArabic: "Tiếng Ả Rập",
    languageVietnamese: "Tiếng Việt",

    welcome: "Chào mừng! Vui lòng đăng nhập để đặt món ăn.",
    phoneNumber: "Số điện thoại",
    enterPhone: "Nhập số điện thoại của quý vị",
    enterPhoneRestaurant: "Nhập số điện thoại đã đăng ký với nhà hàng của quý vị",
    enterEmailRestaurant: "Nhập email đã đăng ký với nhà hàng của quý vị",
    changePhone: "Thay đổi số điện thoại",
    changeEmail: "Thay đổi email",
    verificationCode: "Mã xác thực",
    sendCode: "Gửi mã xác thực",
    invalidCode: "Mã xác thực không hợp lệ. Vui lòng thử lại.",
    sending: "Đang gửi",
    verify: "Xác thực & đăng nhập",
    enterCode: "Nhập mã gồm 6 chữ số",
    enterCodeSentTo: "Nhập mã gồm 6 chữ số được gửi đến",
    pleaseLogin: "Vui lòng đăng nhập để xem và quản lý các đơn đặt hàng của quý vị",
    didntReceiveCode: "Không nhận được mã? Gửi mã lại",
    troubleLoggingIn: "Gặp sự cố khi đăng nhập?",
    error: "Lỗi",
    logout: "Đăng xuất",
    logoutConfirm: "Quý vị có chắc chắn muốn đăng xuất không?",
    cancel: "Hủy",

    familyId: "Mã định danh gia đình",
    participantId: "Mã định danh tham gia",
    yourFamilyOrParticipantId: "Mã định danh gia đình / người tham gia của quý vị",
    yourFamilyCode: "Mã Gia Đình Của Bạn",
    showThisCode: "Hiển thị mã này khi lấy đơn đặt hàng của quý vị",

    voucher: "Phiếu mua hàng",
    vouchersAvailable: "[#] phiếu mua hàng khả dụng cho đến ngày",
    vouchersUsedLegacy: "Phiếu mua hàng đã sử dụng",
    vouchersRemainingLegacy: "Phiếu mua hàng còn lại",
    vouchersOfRemaining: "[#] phiếu mua hàng trong số [#] còn lại",
    noVouchersRemaining: "Không còn phiếu mua hàng nào",
    usedAllVouchers: "Người tham gia này đã sử dụng hết tất cả các phiếu mua hàng khả dụng của họ.",
    usedAllVouchersCycle: "Người tham gia này đã sử dụng hết tất cả các phiếu mua hàng cho chu kỳ bữa ăn này.",
    willReceiveNewVouchers: "Họ sẽ nhận được 8 phiếu mua hàng mới vào ngày [date].",

    meal: "Bữa ăn",
    meals: "Các bữa ăn",
    mealsAvailable: "Còn [#] bữa ăn đến ngày",
    mealsUsed: "Số bữa ăn đã sử dụng",
    mealsRemaining: "Số bữa ăn còn lại",
    mealsOfRemaining: "Còn [#] trong tổng số [#] bữa ăn",
    noMealsRemaining: "Không còn bữa ăn nào",
    usedAllMeals: "Người tham gia này đã sử dụng hết tất cả các bữa ăn hiện có của họ.",
    usedAllMealsCycle: "Người tham gia này đã sử dụng hết tất cả các bữa ăn cho chu kỳ bữa ăn này.",
    willReceiveNewMeals: "Họ sẽ nhận được 8 bữa ăn mới vào ngày [date].",
    vouchers: "Các bữa ăn khả dụng",
    availableUntil: "có sẵn trước",

    menu: "Thực đơn",
    menuItems: "Các món trong thực đơn",
    selectMenuItems: "Chọn các món trong thực đơn",
    open: "Mở cửa",
    closed: "Đóng cửa",
    browse: "Duyệt",
    cart: "Giỏ hàng",
    viewCart: "Xem giỏ hàng",
    cartEmpty: "Giỏ hàng của quý vị đang trống",
    cartChangeHint: "Để thay đổi đơn đặt hàng, hãy xóa một món ăn khỏi giỏ hàng bên dưới.",
    order: "Đơn đặt hàng",
    orders: "Các đơn đặt hàng",
    addToOrder: "Thêm vào đơn đặt hàng",
    addToCart: "Thêm vào đơn đặt hàng",
    notEnoughMeals: "Không đủ bữa ăn",
    currentlyClosed: "Hiện đang đóng cửa",
    closedBanner: "Nhà hàng này hiện đang đóng cửa. Quý vị có thể xem thực đơn, nhưng chỉ có thể đặt hàng trong giờ mở cửa.",
    yourOrder: "Đơn đặt hàng của quý vị",
    itemsOrdered: "Các món đã đặt",
    item: "Món ăn",
    items: "Các món ăn",
    reviewRemoveItems: "Xem lại / xóa các món ăn",
    review: "Xem lại",
    remove: "Xóa",
    placeOrder: "Đặt hàng",
    placingOrder: "Đang đặt hàng",
    checkout: "Đặt hàng",
    activeOrders: "Các đơn đặt hàng đang hoạt động",
    ordersToday: "Các đơn đặt hàng hôm nay",
    previousOrders: "Các đơn đặt hàng trước đó",
    totalOrdersToday: "Tổng cộng có [#] đơn đặt hàng hôm nay",
    walkInOrder: "Đơn hàng trực tiếp tại nhà hàng",
    redemptionCardOrder: "Đơn đặt hàng bằng thẻ quy đổi",
    myOrders: "Đơn Hàng Của Tôi",
    myCart: "Giỏ Hàng Của Tôi",
    noActiveOrders: "Không có đơn hàng đang hoạt động",
    noActiveOrdersMessage: "Bạn không có đơn hàng nào đang chờ nhận. Duyệt nhà hàng để đặt đơn hàng mới!",
    browseRestaurants: "Duyệt nhà hàng",
    onlyOneRestaurantAtATime: "Mỗi lần chỉ có thể lấy các đơn đặt hàng từ một nhà hàng.",
    orderNumber: "Đơn Hàng #",
    orderStatus: "Trạng Thái",
    statusPending: "Đang Chờ",
    statusConfirmed: "Đã xác nhận",
    statusReady: "Đã sẵn sàng để lấy hàng",
    confirmed: "Đã xác nhận",
    canceled: "Đã hủy",
    cancelOrder: "Hủy đơn đặt hàng",
    waiting: "Đang chờ",
    completed: "Đã hoàn thành",
    pickedUp: "Đã lấy hàng",
    readyForPickup: "Đã sẵn sàng để lấy hàng",
    markAsPickedUp: "Đánh dấu là đã lấy hàng",
    callRestaurant: "Gọi nhà hàng",
    loading: "Đang tải nhà hàng...",
    orderSuccess: "Đặt hàng thành công!",
    orderAddedToActive: "Đơn đặt hàng đã được thêm vào Các đơn đặt hàng đang hoạt động của quý vị.",
    redirectingToActiveOrders: "Đang chuyển hướng đến Các đơn đặt hàng đang hoạt động...",
    pleasePrepareOrder: "Vui lòng chuẩn bị đơn hàng ngay lập tức.",

    search: "Tìm kiếm nhà hàng...",
    allNeighborhoods: "Tất cả các khu vực",
    filterByNeighborhood: "Lọc theo khu vực",
    allCuisines: "Tất cả các loại ẩm thực",
    filterByCuisine: "Lọc theo ẩm thực",
    otherFilters: "Các bộ lọc khác",
    filterBy: "Lọc theo",
    restaurantHours: "Giờ mở cửa của nhà hàng",
    noRestaurantsFiltered: "Không có nhà hàng nào phù hợp với bộ lọc",
    noRestaurantsFilteredMessage: "Hãy thử điều chỉnh bộ lọc khu vực hoặc ẩm thực để xem thêm tùy chọn.",
    clearFilters: "Xóa tất cả bộ lọc",

    orderConfirmed: "Đơn đặt hàng của quý vị đã được xác nhận!",
    orderPlaced: "Đã đặt hàng",
    orderPlacedAt: "Đơn hàng được đặt lúc",
    orderPlacedAtRestaurant: "Đơn hàng được đặt tại [restaurant]",
    orderDetails: "Chi tiết đơn đặt hàng",
    orderedAt: "Đã đặt lúc",
    pickupAt: "Lấy hàng lúc",
    pickupBy: "Lấy hàng trước",
    pickupOnly: "Chỉ lấy hàng trực tiếp",
    pickupInstructions: "Hướng dẫn lấy hàng",
    pickupLocation: "Địa điểm lấy hàng",
    pickupWindow: "Khung giờ lấy hàng",
    restaurantLocation: "Địa điểm lấy hàng",
    getDirections: "Tìm đường đi",
    youNowHave: "Quý vị hiện còn lại",
    mealsLeftBefore: "bữa ăn trước khi",
    mealLeftBefore: "bữa ăn trước khi",
    total: "Tổng cộng",
    pickupWindowSentence: "Quý vị có thể lấy đơn đặt hàng của mình trong vòng [time] và không muộn hơn [time], trừ khi nhà hàng đóng cửa sớm hơn.",
    inOneHour: "1 giờ",
    in30Minutes: "30 phút",
    in60Minutes: "60 phút",
    noLaterThan: "không muộn hơn 90 phút",
    in90Minutes: "90 phút",
    unlessCloseSooner: "trừ khi đóng cửa sớm hơn",
    asSoonAsPossible: "Càng sớm càng tốt",
    pickupAsap: "Lấy hàng càng sớm càng tốt",
    familyMealLastOrder: "Đặt hàng Family Meals cuối cùng lúc",
    pickedUpBadge: "Đã lấy hàng",
    readyForPickup: "Sẵn sàng để lấy",
    goHome: "Đi đến Trang chủ",
    back: "Quay lại",

    help: "Trợ giúp",
    needHelp: "Cần trợ giúp?",
    contactSupport: "Liên Hệ Hỗ Trợ",
    callUs: "Đường dây hỗ trợ SF New Deal",
    officeHours: "Giờ làm việc của văn phòng SF New Deal",
    mondayFriday: "Thứ Hai - Thứ Sáu",
    hours: "9:00 SA - 5:00 CH",
    monday: "Thứ Hai",
    tuesday: "Thứ Ba",
    wednesday: "Thứ Tư",
    thursday: "Thứ Năm",
    friday: "Thứ Sáu",
    saturday: "Thứ Bảy",
    sunday: "Chủ Nhật",
    voicemailInstructions: "Nếu không có ai sẵn sàng hỗ trợ hoặc bạn gọi ngoài những khung giờ này, hãy để lại lời thoại kèm theo tên và số điện thoại của quý vị, và nhân viên trong đội của chúng tôi sẽ gọi lại cho quý vị.",
    callCenterTagline: "Đặt các bữa ăn ngon, phù hợp với văn hóa từ hơn 30 nhà hàng trên khắp San Francisco.",
    needSupport: "Nếu bạn cần hỗ trợ, vui lòng gọi đường dây hỗ trợ SF New Deal tại",
    contactCallCenter: "Hãy gọi tổng đài SF New Deal theo số (415) 480-1185.",
    extensions: "Bấm phím #2 cho tiếng Quảng, #3 cho tiếng Quan thoại, #4 cho tiếng Tây Ban Nha.",
    commonQuestions: "Các câu hỏi thường gặp",
    howToOrder: "Làm cách nào để đặt bữa?",
    howToOrderAnswer: "Duyệt các nhà hàng sẵn có, thêm các món ăn vào giỏ hàng của quý vị và nhấn 'Đặt hàng'. Quý vị sẽ nhận được một xác nhận kèm theo hướng dẫn lấy hàng.",
    wherePickup: "Tôi lấy đơn đặt hàng của mình ở đâu?",
    wherePickupAnswer: "Màn hình xác nhận của quý vị hiển thị địa chỉ nhà hàng và nút 'Tìm đường' để giúp bạn đến đó.",
    whenPickup: "Khi nào tôi có thể lấy đơn đặt hàng của mình?",
    whenPickupAnswer: "Đối với các đơn đặt hàng từ 1 đến 6 bữa ăn, quý vị có thể lấy các bữa ăn của mình sau 30 phút. Đối với các đơn đặt hàng trên 6 bữa ăn, quý vị có thể lấy hàng sau 60 phút kể từ khi đặt hàng. Quý vị phải lấy hàng trong vòng 90 phút kể từ khi đặt hàng, trừ khi nhà hàng đóng cửa sớm hơn.",
    whatBring: "Tôi phải làm gì khi đến lấy các bữa ăn của mình?",
    whatBringAnswer: "Khi bạn đến nhà hàng, hãy nói với nhân viên rằng quý vị sử dụng Family Meal Pack Program và chia sẻ mã định danh gia đình của mình. Họ sẽ đối chiếu bạn với đơn đặt hàng chính xác và giao các bữa ăn cho quý vị.",
    howManyMeals: "Tôi có thể đặt bao nhiêu bữa?",
    howManyMealsAnswer: "Hãy kiểm tra số bữa ăn còn lại của quý vị ở đầu màn hình chính.",
    mealsResetSentence: "Các bữa ăn của quý vị sẽ được làm mới vào đầu mỗi chu kỳ 2 tuần. Các bữa ăn không được chuyển nhượng hoặc cộng dồn từ chu kỳ này sang chu kỳ tiếp theo.",
    cycleDatesSentence: "Có thể tìm thấy ngày bắt đầu và ngày kết thúc chu kỳ tại đây.",

    verifyWithTwoStep: "Xác thực người tham gia bằng quy trình hai bước",
    step1EnterFamilyId: "Bước 1: Nhập mã định danh gia đình / người tham gia của họ",
    askParticipantForFamilyId: "Yêu cầu người tham gia cung cấp mã định danh gia đình / người tham gia của họ",
    verifyFamilyIdButton: "Xác thực mã định danh gia đình / người tham gia",
    verifying: "Đang xác thực",
    familyIdVerified: "Mã định danh gia đình / người tham gia đã được xác thực",
    familyIdNotFound: "Không tìm thấy mã định danh gia đình / người tham gia. Vui lòng kiểm tra lại với gia đình và nhập lại.",
    step2VerifyPhone: "Bước 2: Xác thực số điện thoại",
    askLastFourDigits: "Yêu cầu người tham gia cung cấp 4 chữ số cuối trong số điện thoại của họ",
    lastFourDigits: "4 chữ số cuối",
    phoneDoesNotMatch: "Số điện thoại không trùng khớp. Vui lòng xác nhận lại 4 chữ số cuối với người tham gia.",
    startOver: "Bắt đầu lại",

    dietaryConsiderations: "Lưu ý về chế độ ăn uống",
    dietaryRestrictions: "Hạn chế về chế độ ăn uống",
    yourDietaryRestrictions: "Hạn chế về chế độ ăn uống của quý vị",
    noDietaryRestrictions: "Không có hạn chế về chế độ ăn uống",
    dietVegetarian: "Ăn chay",
    dietVegan: "Ăn chay thuần",
    dietNutAllergy: "Dị ứng các loại hạt",
    dietSeafoodAllergy: "Dị ứng hải sản",
    dietLactoseIntolerant: "Dị ứng đường sữa",
    dietGlutenAllergy: "Dị ứng gluten",
    dietDiabetic: "Tiểu đường",
    dietHalal: "Thực phẩm Halal",
    dietNoPork: "Không ăn thịt lợn",
    dietCitrusAllergy: "Dị ứng cam quýt",
    dietNoEgg: "Không ăn trứng",
    dietSoyAllergy: "Dị ứng đậu nành",
    dietAvocadoAllergy: "Dị ứng quả bơ",
    dietScallopAllergy: "Dị ứng sò điệp",
    dietCinnamonAllergy: "Dị ứng quế",
    dietGrapeseedOilAllergy: "Dị ứng dầu hạt nho",
    dietPineappleAllergy: "Dị ứng dứa",
    dietHoneyAllergy: "Dị ứng mật ong",
    dietFavaBeansAllergy: "Dị ứng đậu tằm",
    dietTomatoAllergy: "Dị ứng cà chua",
    dietOtherAllergy: "Dị ứng khác",
    dietNoBeef: "Không ăn thịt bò",
    dietDairyFree: "Không có sữa",
    dietGlutenFree: "Không có Gluten",

    cuisine: "Ẩm thực",
    cuisineAmerican: "Món Mỹ",
    cuisineChinese: "Món Trung Quốc",
    cuisineJapanese: "Món Nhật",
    cuisineBurmese: "Món Miến Điện",
    cuisineLatinAmerican: "Món Mỹ Latinh",
    cuisineGuatemalan: "Món Guatemala",
    cuisineMexican: "Món Mexico",
    cuisineSalvadoran: "Món El Salvador",
    cuisineVenezuelan: "Món Venezuela",
    cuisineIndian: "Món Ấn Độ",
    cuisineMediterranean: "Món Địa Trung Hải",
    cuisineMiddleEastern: "Món Trung Đông",
    cuisineNorthAfrican: "Món Bắc Phi",
    cuisineYemeni: "Món Yemen",
    cuisineVietnamese: "Món Việt",
    cuisineFusion: "Ẩm thực kết hợp"
  },

  ar: {
    title: "برنامج الوجبات العائلية",
    programName: "برنامج الوجبات العائلية",
    sfNewDeal: "SF New Deal",
    sfhsa: "وكالة الخدمات الإنسانية التابعة لمدينة سان فرانسيسكو (SFHSA)",
    orderingPlatform: "منصة الطلب",
    restaurantDashboard: "لوحة تحكم المطعم",
    redemptionCard: "بطاقة استرداد",
    smsOrderSystem: "نظام الطلب عبر الرسائل النصية",
    texting: "الرسائل النصية",
    sms: "رسالة نصية قصيرة",
    mobileApp: "تطبيق الهاتف المحمول",
    interface: "واجهة المستخدم",
    callCenter: "مركز الاتصال",
    supportLine: "خط الدعم",

    language: "اللغة",
    languageEnglish: "الإنجليزية",
    languageSpanish: "الإسبانية",
    languageChinese: "الصينية",
    languageArabic: "العربية",
    languageVietnamese: "الفيتنامية",

    welcome: "مرحبا! يرجى تسجيل الدخول لطلب وجبات الطعام",
    phoneNumber: "رقم الهاتف",
    enterPhone: "أدخل رقم هاتفك",
    enterPhoneRestaurant: "أدخل رقم الهاتف المسجل لدى مطعمك",
    enterEmailRestaurant: "أدخل البريد الإلكتروني المسجل لدى مطعمك",
    changePhone: "تغيير رقم الهاتف",
    changeEmail: "تغيير البريد الإلكتروني",
    verificationCode: "رمز التحقق",
    sendCode: "إرسال رمز التحقق",
    invalidCode: "رمز التحقق غير صالح. يرجى إعادة المحاولة.",
    sending: "جاري الإرسال",
    verify: "التحقق وتسجيل الدخول",
    enterCode: "أدخل الرمز المكون من 6 أرقام",
    enterCodeSentTo: "أدخل الرمز المكون من 6 أرقام المرسل إلى",
    pleaseLogin: "يرجى تسجيل الدخول لعرض طلباتك وإدارتها",
    didntReceiveCode: "لم تستلم رمزًا؟ إعادة الإرسال",
    troubleLoggingIn: "تواجه مشكلة في تسجيل الدخول؟",
    error: "خطأ",
    logout: "تسجيل الخروج",
    logoutConfirm: "هل أنت متأكد أنك تريد تسجيل الخروج؟",
    cancel: "إلغاء",

    familyId: "معرف العائلة",
    participantId: "معرف المشارك",
    yourFamilyOrParticipantId: "معرف عائلتك / معرف المشارك",
    yourFamilyCode: "رمز عائلتك",
    showThisCode: "أظهر هذا الرمز عند استلام طلبك",

    voucher: "قسيمة",
    vouchersAvailable: "[#] قسائم متاحة حتى",
    vouchersUsedLegacy: "القسائم المستخدمة",
    vouchersRemainingLegacy: "القسائم المتبقية",
    vouchersOfRemaining: "تبقى [#] قسائم من أصل [#]",
    noVouchersRemaining: "لا توجد قسائم متبقية",
    usedAllVouchers: "استخدم هذا المشارك جميع قسائمه المتاحة.",
    usedAllVouchersCycle: "استخدم هذا المشارك جميع قسائمه لهذه الدورة من الوجبات.",
    willReceiveNewVouchers: "سيتم استلام 8 قسائم جديدة في [date].",

    meal: "وجبة",
    meals: "وجبات",
    mealsAvailable: "[#] وجبات متاحة حتى",
    mealsUsed: "الوجبات المستخدمة",
    mealsRemaining: "الوجبات المتبقية",
    mealsOfRemaining: "تبقى [#] وجبات من أصل [#]",
    noMealsRemaining: "لا توجد وجبات متبقية",
    usedAllMeals: "استخدم هذا المشارك جميع وجباته المتاحة.",
    usedAllMealsCycle: "استخدم هذا المشارك جميع وجباته لهذه الدورة.",
    willReceiveNewMeals: "سيستلم 8 وجبات جديدة في [date].",
    vouchers: "الوجبات المتاحة",
    availableUntil: "متاحة قبل",

    menu: "قائمة الطعام",
    menuItems: "أصناف قائمة الطعام",
    selectMenuItems: "حدد أصناف قائمة الطعام",
    open: "مفتوح",
    closed: "مغلق",
    browse: "تصفح",
    cart: "السلة",
    viewCart: "عرض السلة",
    cartEmpty: "سلة التسوق فارغة",
    cartChangeHint: "لتغيير الطلب، احذف صنفًا من السلة أدناه.",
    order: "طلب",
    orders: "طلبات",
    addToOrder: "الإضافة إلى الطلب",
    addToCart: "الإضافة إلى الطلب",
    notEnoughMeals: "الوجبات غير كافية",
    currentlyClosed: "مغلق حالياً",
    closedBanner: "هذا المطعم مغلق حالياً. يمكنك تصفح القائمة، ولكن يمكنك تقديم الطلب فقط خلال ساعات العمل.",
    yourOrder: "طلبك",
    itemsOrdered: "الأصناف المطلوبة",
    item: "صنف",
    items: "أصناف",
    reviewRemoveItems: "مراجعة / حذف أصناف",
    review: "مراجعة",
    remove: "حذف",
    placeOrder: "إتمام الطلب",
    placingOrder: "جاري إتمام الطلب",
    checkout: "إتمام الطلب",
    activeOrders: "الطلبات النشطة",
    ordersToday: "طلبات اليوم",
    previousOrders: "الطلبات السابقة",
    totalOrdersToday: "[#] إجمالي الطلبات اليوم",
    walkInOrder: "طلب حضوري",
    redemptionCardOrder: "طلب باستخدام بطاقة استرداد",
    myOrders: "طلباتي",
    myCart: "سلتي",
    noActiveOrders: "لا توجد طلبات نشطة",
    noActiveOrdersMessage: "ليس لديك أي طلبات في انتظار الاستلام. تصفح المطاعم لتقديم طلب جديد!",
    browseRestaurants: "تصفح المطاعم",
    onlyOneRestaurantAtATime: "يمكن استلام الطلبات من مطعم واحد فقط في كل مرة.",
    orderNumber: "رقم الطلب #",
    orderStatus: "الحالة",
    statusPending: "قيد الانتظار",
    statusConfirmed: "مؤكد",
    statusReady: "جاهز للاستلام",
    confirmed: "مؤكد",
    canceled: "ملغى",
    cancelOrder: "إلغاء الطلب",
    waiting: "في الانتظار",
    completed: "مكتمل",
    pickedUp: "تم الاستلام",
    readyForPickup: "جاهز للاستلام",
    markAsPickedUp: "تحديد كـ تم الاستلام",
    callRestaurant: "اتصل بالمطعم",
    loading: "جاري تحميل المطاعم...",
    orderSuccess: "تم تقديم الطلب بنجاح!",
    orderAddedToActive: "تمت إضافة الطلب إلى طلباتك النشطة.",
    redirectingToActiveOrders: "جاري إعادة التوجيه إلى الطلبات النشطة...",
    pleasePrepareOrder: "يرجى تجهيز الطلب فورا.",

    search: "البحث عن المطاعم...",
    allNeighborhoods: "جميع الأحياء",
    filterByNeighborhood: "تصفية حسب الحي",
    allCuisines: "جميع المطابخ",
    filterByCuisine: "تصفية حسب المطبخ",
    otherFilters: "فلاتر أخرى",
    filterBy: "تصفية حسب",
    restaurantHours: "ساعات عمل المطعم",
    noRestaurantsFiltered: "لا توجد مطاعم تطابق الفلاتر الخاصة بك",
    noRestaurantsFilteredMessage: "حاول تعديل فلاتر الحي أو المطبخ لمشاهدة المزيد من الخيارات.",
    clearFilters: "مسح كل الفلاتر",

    orderConfirmed: "تم تأكيد طلبك!",
    orderPlaced: "تم تقديم الطلب",
    orderPlacedAt: "تم تقديم الطلب في",
    orderPlacedAtRestaurant: "تم تقديم الطلب في [restaurant]",
    orderDetails: "تفاصيل الطلب",
    orderedAt: "تم الطلب في",
    pickupAt: "الاستلام في",
    pickupBy: "الاستلام قبل",
    pickupOnly: "الاستلام فقط",
    pickupInstructions: "تعليمات الاستلام",
    pickupLocation: "موقع الاستلام",
    pickupWindow: "وقت الاستلام",
    restaurantLocation: "موقع الاستلام",
    getDirections: "الحصول على الاتجاهات",
    youNowHave: "تبقى لديك",
    mealsLeftBefore: "وجبة قبل",
    mealLeftBefore: "وجبة قبل",
    total: "الإجمالي",
    pickupWindowSentence: "يمكنك استلام طلبك خلال [time]، على ألا يتجاوز ذلك [time]، إلا إذا كان المطعم سيغلق قبل ذلك.",
    inOneHour: "ساعة واحدة",
    in30Minutes: "30 دقيقة",
    in60Minutes: "60 دقيقة",
    noLaterThan: "في موعد أقصاه 90 دقيقة",
    in90Minutes: "90 دقيقة",
    unlessCloseSooner: "ما لم يُغلق في وقت أقرب",
    asSoonAsPossible: "في أقرب وقت ممكن",
    pickupAsap: "الاستلام في أقرب وقت ممكن",
    familyMealLastOrder: "آخر طلب لـ Family Meals في",
    pickedUpBadge: "تم الاستلام",
    readyForPickup: "جاهز للاستلام",
    goHome: "العودة إلى الصفحة الرئيسية",
    back: "رجوع",

    help: "مساعدة",
    needHelp: "تحتاج مساعدة؟",
    contactSupport: "اتصل بالدعم",
    callUs: "خط دعم SF New Deal",
    officeHours: "ساعات عمل مكتب SF New Deal",
    mondayFriday: "الاثنين - الجمعة",
    hours: "9:00 صباحاً - 5:00 مساءً",
    monday: "الاثنين",
    tuesday: "الثلاثاء",
    wednesday: "الأربعاء",
    thursday: "الخميس",
    friday: "الجمعة",
    saturday: "السبت",
    sunday: "الأحد",
    voicemailInstructions: "في حال عدم وجود من يساعدك أو كنت تتصل خارج هذه الأوقات، اترك رسالة صوتية باسمك ورقم هاتفك، وسيتصل بك أحد أعضاء فريقنا.",
    callCenterTagline: "اطلب وجبات لذيذة ومناسبة ثقافيا من أكثر من 30 مطعمًا في سان فرانسيسكو.",
    needSupport: "إذا كنت بحاجة إلى الدعم، يرجى الاتصال بخط دعم SF New Deal على",
    contactCallCenter: "يرجى الاتصال بمركز اتصال SFND على رقم (415) 480-1185.",
    extensions: "اضغط #2 للكانتونية، #3 للماندرين، #4 للإسبانية.",
    commonQuestions: "الأسئلة الشائعة للعائلات",
    howToOrder: "من أين يمكنني طلب وجباتي؟",
    howToOrderAnswer: "تصفح المطاعم المتاحة، أضف الأصناف إلى سلة التسوق، ثم انقر فوق \"إنشاء الطلب\". ستتلقى رسالة تأكيد مع تعليمات الاستلام.",
    wherePickup: "أين أستلم طلبي؟",
    wherePickupAnswer: "تظهر شاشة التأكيد الخاصة بك عنوان المطعم وزر \"الحصول على الاتجاهات\" لمساعدتك في الوصول إليه.",
    whenPickup: "متى يمكنني استلام طلبي؟",
    whenPickupAnswer: "للطلبات التي تتراوح بين 1 و 6 وجبات، يمكنك استلام وجباتك بعد 30 دقيقة. أما الطلبات التي تزيد عن 6 وجبات، فيمكنك استلام طلبك بعد 60 دقيقة من تقديم الطلب. يجب عليك استلام طلبك خلال 90 دقيقة من تقديم الطلب، إلا إذا كان المطعم سيغلق أبواب قبل ذلك.",
    whatBring: "ماذا أفعل عند استلام وجباتي؟",
    whatBringAnswer: "عند وصولك إلى المطعم، يرجى إبلاغ موظفي المطعم بأنك مشترك في برنامج الوجبات العائلية، وأن تشاركهم بمعرف العائلة/المشارك. سيقومون بمطابقتك مع الطلب الصحيح وتسليمك وجباتك.",
    howManyMeals: "كم عدد الوجبات التي يمكنني طلبها؟",
    howManyMealsAnswer: "تحقق من وجباتك المتاحة أعلى الشاشة الرئيسية.",
    mealsResetSentence: "يتم إعادة ضبط وجباتك في بداية كل دورة مدتها أسبوعان. لا يتم نقل الوجبات أو ترحيلها من دورة إلى أخرى.",
    cycleDatesSentence: "يمكنك الاطلاع على تاريخ بداية ونهاية الدورة هنا.",

    verifyWithTwoStep: "تحقق من هوية المشارك بخطوتين",
    step1EnterFamilyId: "الخطوة 1: أدخل معرف العائلة / معرف المشارك",
    askParticipantForFamilyId: "اطلب من المشارك معرف العائلة / معرف المشارك",
    verifyFamilyIdButton: "تحقق من معرف العائلة / معرف المشارك",
    verifying: "جاري التحقق",
    familyIdVerified: "تم التحقق من معرف العائلة / معرف المشارك",
    familyIdNotFound: "لم يتم العثور على معرف العائلة / معرف المشارك. يرجى إعادة التأكد مع العائلة وإعادة الإدخال.",
    step2VerifyPhone: "الخطوة 2: تحقق من رقم الهاتف",
    askLastFourDigits: "اطلب من المشارك آخر 4 أرقام من رقم هاتفه",
    lastFourDigits: "آخر 4 أرقام",
    phoneDoesNotMatch: "رقم الهاتف غير مطابق. يرجى إعادة تأكيد آخر 4 أرقام مع المشارك.",
    startOver: "ابدأ من جديد",

    dietaryConsiderations: "اعتبارات غذائية",
    dietaryRestrictions: "قيود غذائية",
    yourDietaryRestrictions: "قيودك الغذائية",
    noDietaryRestrictions: "لا يوجد قيود غذائية",
    dietVegetarian: "نباتي",
    dietVegan: "نباتي صرف",
    dietNutAllergy: "حساسية المكسرات",
    dietSeafoodAllergy: "حساسية من المأكولات البحرية",
    dietLactoseIntolerant: "عدم تحمل اللاكتوز",
    dietGlutenAllergy: "حساسية الغلوتين أو القمح",
    dietDiabetic: "مرض سكري",
    dietHalal: "حلال",
    dietNoPork: "بدون لحم خنزير",
    dietCitrusAllergy: "حساسية الحمضيات",
    dietNoEgg: "بدون بيض",
    dietSoyAllergy: "حساسية الصويا",
    dietAvocadoAllergy: "حساسية الأفوكادو",
    dietScallopAllergy: "حساسية الأسقلوب",
    dietCinnamonAllergy: "حساسية القرفة",
    dietGrapeseedOilAllergy: "حساسية زيت بذور العنب",
    dietPineappleAllergy: "حساسية الأناناس",
    dietHoneyAllergy: "حساسية العسل",
    dietFavaBeansAllergy: "حساسية الفول",
    dietTomatoAllergy: "حساسية الطماطم",
    dietOtherAllergy: "حساسية أخرى",
    dietNoBeef: "بدون لحم بقري",
    dietDairyFree: "خالٍ من منتجات الألبان",
    dietGlutenFree: "خالٍ من الغلوتين",

    cuisine: "المطبخ",
    cuisineAmerican: "أمريكي",
    cuisineChinese: "صيني",
    cuisineJapanese: "ياباني",
    cuisineBurmese: "بورمي",
    cuisineLatinAmerican: "أمريكي لاتيني",
    cuisineGuatemalan: "غواتيمالي",
    cuisineMexican: "مكسيكي",
    cuisineSalvadoran: "سلفادوري",
    cuisineVenezuelan: "فنزويلي",
    cuisineIndian: "هندي",
    cuisineMediterranean: "متوسطي",
    cuisineMiddleEastern: "شرق أوسطي",
    cuisineNorthAfrican: "شمال أفريقي",
    cuisineYemeni: "يمني",
    cuisineVietnamese: "فيتنامي",
    cuisineFusion: "مزيج من المطابخ"
  }
};

// Seed every language with the English error fallbacks (PENDING_TRANSLATION_ERRORS),
// then let each block's own keys override — so when translated copy lands for a
// language it simply takes precedence and no caller changes.
for (const lang of Object.keys(TRANSLATIONS)) {
  TRANSLATIONS[lang] = { ...PENDING_TRANSLATION_ERRORS, ...TRANSLATIONS[lang] };
}

export default TRANSLATIONS;
